import { createLogger } from '@core/services/logger.service';
import * as Orm from '@core/services/orm/orm';
import _PatchHandlerService from '@archipad/backend/project/patch';
import { MutablePatch, PatchHandler, PatchHandlerContext, PatchId } from '@archipad-js/core/patch'
import { AnnotatedEntity, AnnotationEntity } from '@archipad-js/archipad/entity-services';
import { VisitEntity, BugEntity, BugNoteEntity, PersonEntity, promoimmoEntity, ReportEntity, RegionMemberEntity, BugAnnotationEntity, BugNoteAnnotationEntity, RemarkAnnotationEntity } from '@archipad-js/archipad-model';

import { EntityType } from '@archipad-js/core/orm';

const log = createLogger('tempEntities');

export function isEmptyTempVisit(visit:VisitEntity):boolean {
    return (visit.temp && 
        !visit.object &&
        !visit.region &&
        !visit.notes &&
        !(visit.promoimmo && !isPromoimmoEmpty(visit.promoimmo)) &&
        !containsEntity(visit.bugs, (bug) => !isEmptyTempBug(bug)) &&
        !visit.members.length &&
        !containsEntity(visit.closingBugs, (bug) => !isEmptyTempBug(bug)) &&
        !containsEntity(visit.bugNotes, (bugNote) => !isEmptyTempBugNote(bugNote)) &&
        !visit.remarks.length &&
        !visit.closingRemarks.length &&
        !visit.forms.length
    );
}
export function isPersonEmpty(person:PersonEntity):boolean {
    return (
        !person.name && 
        !person.email &&
        !person.abbreviation &&
        !person.address &&
        !person.company &&
        !person.phone && 
        !person.memberOfRegions?.find(regionMember => !isRegionMemberEmpty(regionMember)) &&
        !person.memberOfVisit.length
    );
}

export function isRegionMemberEmpty(regionMember: RegionMemberEntity){
    return regionMember.type == null;
}

export function isPromoimmoEmpty(promoimmo: promoimmoEntity): boolean {
    for (const [propertyName, propertyDescription] of Object.entries(promoimmo.entityType.metadata)) {
        if (propertyDescription.relation === false && propertyDescription.primaryKey) {
            continue;
        }

        if (!propertyName.startsWith('promoimmo_')) {
            continue;
        }

        if (propertyName in promoimmo && promoimmo[propertyName]) {
            return false;
        }
    }

    return true;
}
export function isEmptyTempBug(bug:BugEntity):boolean {
    return (
        bug.temp &&
        !bug.text &&
        !bug.index &&
        !bug.hasAttachments() &&
        !bug.relatedDocuments?.length &&
        isAnnotatedEntityWithoutAnnotation(bug) &&
        !containsEntity(bug.notes, (bugNote) => !isEmptyTempBugNote(bugNote))
    );
}
export function isEmptyTempBugNote(bugNote:BugNoteEntity):boolean {
    return (
        (bugNote.temp || bugNote.bug.temp) &&   // bug note is considered temp if it belong to a temp bug
        !bugNote.text &&
        isAnnotatedEntityWithoutAnnotation(bugNote) &&
        !bugNote.hasAttachments()
    );
}

export function isTempReport(report: ReportEntity): boolean {
    return (
        report.temp
    );
}

export function isAnnotatedEntityWithoutAnnotation(annotatedEntity: AnnotatedEntity): boolean {
    return (
        !containsEntity<AnnotationEntity>(annotatedEntity.annotations, (annotation) => !isEmptyTempAnnotationEntity(annotation))
    );
}

export function isEmptyTempAnnotationEntity(annotation: AnnotationEntity): boolean {
    return (
        !annotation.hasAttachments() &&
        isTempAnnotationEntity(annotation)
    );
}

export function isTempAnnotationEntity(annotationEntity: AnnotationEntity): boolean {
    return annotationEntity.temp;
}

function containsEntity<T>(entities:ReadonlyArray<T>, fn:(entity:T)=>boolean):boolean {
    for(const entity of entities) {
        if(fn(entity))
            return true;
    }
    return false;
}

//------------------------------------------------------------------------------
export class TempEntitiesHandler implements PatchHandler {
    async willExportPatch(entityContext: Orm.EntityContext, projectId: string, patchHandlerContext: PatchHandlerContext, patch:MutablePatch, patchId?: PatchId): Promise<void> {
        if (patchHandlerContext !== PatchHandlerContext.TEMP){
            this._handleTempEntities(entityContext, patch);
        }
    }
    
    //-------------------------------------------------------------------------
    private _isEmptyTempEntity(entityType:EntityType, entity:Orm.BaseEntity) {
        switch(entityType.entityName) {
            case 'Visit': return isEmptyTempVisit(entity as VisitEntity); 
            case 'Person': return isPersonEmpty(entity as PersonEntity); 
            case 'promoimmo': return isPromoimmoEmpty(entity as promoimmoEntity); 
            case 'Bug': return isEmptyTempBug(entity as BugEntity); 
            case 'BugNote': return isEmptyTempBugNote(entity as BugNoteEntity);
            case 'Report': return isTempReport(entity as ReportEntity);
            case 'BugAnnotation': return isTempAnnotationEntity(entity as BugAnnotationEntity);
            case 'BugNoteAnnotation': return isTempAnnotationEntity(entity as BugNoteAnnotationEntity);
            case 'RemarkAnnotation': return isTempAnnotationEntity(entity as RemarkAnnotationEntity);
            default: return false;
        }
    }
    
    //-------------------------------------------------------------------------

    private _handleTempEntities(entityContext:Orm.EntityContext, patch:MutablePatch) {
        const removedTempEntitiesByType = new Map<string,Set<string>>();
        removedTempEntitiesByType.set("Visit", new Set<string>());
        removedTempEntitiesByType.set("Person", new Set<string>());
        removedTempEntitiesByType.set("promoimmo", new Set<string>());
        removedTempEntitiesByType.set("Bug", new Set<string>());
        removedTempEntitiesByType.set("BugNote", new Set<string>());
        removedTempEntitiesByType.set("Report", new Set<string>());
        removedTempEntitiesByType.set("BugAnnotation", new Set<string>());
        removedTempEntitiesByType.set("BugNoteAnnotation", new Set<string>());
        removedTempEntitiesByType.set("RemarkAnnotation", new Set<string>());

        // remove empty temp entities and fix relations
        for(const entityType of entityContext.entityTypes.byIndex) {
            const entityName = entityType.entityName;
            const operations = patch.operationsByType.get(entityType.entityName);
            if(!operations)
                continue;

            operations.operationById.forEach((operation, entityId) => {
                if(operation.type === 'create') {
                    // only new entities may be temp
                    const entity = entityContext.getEntity(entityName,entityId);
                    if(entity && this._isEmptyTempEntity(entityType, entity)) {
                        // empty temp entity: remove from patch
                        removedTempEntitiesByType.get(entityName).add(entityId);
                        operations.operationById.delete(entityId);
                        log.debug(`Removing empty temp entity '${entityName}'(${entityId}): ${entity}`);
                        return;
                    }
                } 

                if(operation.type === 'create' || operation.type === 'update') {
                    // also clear relations on entities that reference removed temp entities
                    switch(entityName) {
                        case 'RegionMember':
                        case 'ProjectMember': 
                        case 'VisitMember': {
                            if(operation.propertyUpdates.has('person')) {
                                if(removedTempEntitiesByType.get('Person').has(operation.propertyUpdates.get('person')))
                                    operation.propertyUpdates.set('person', null);
                            }
                        } break;
                        case 'WorkPackage': {
                            if(operation.propertyUpdates.has('contact')) {
                                if(removedTempEntitiesByType.get('Person').has(operation.propertyUpdates.get('contact')))
                                    operation.propertyUpdates.set('contact', null);
                            }
                        } break;
                    }
                }
            });
        }
    }
}

_PatchHandlerService.registerPatchHandler(new TempEntitiesHandler());
